import { defineStore } from "pinia";
import { doc, query, collection, updateDoc, addDoc, where, setDoc } from "firebase/firestore";
import { db, converter, auth, funcs } from "@/firebase";
import { getRootCollection } from "@/firebase/collections";
import { useFirestore } from "@vueuse/firebase";
import { useAdminStore } from "./admin";
import { computed } from "vue";
import { FBUser, Operator } from "@/models";
import { useToast } from "vue-toastification";
import { createUserWithEmailAndPassword, deleteUser, getAuth, sendPasswordResetEmail, UserCredential } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import dayjs from "dayjs";
import * as Sentry from "@sentry/vue";

const toast = useToast();

export const useUsersStore = defineStore("users", () => {
  const adminStore = useAdminStore();
  const companyEnv = computed(() => adminStore.user?.environment);

  const usersQuery = computed(
    () =>
      !!companyEnv.value &&
      query(
        collection(db, getRootCollection('USERS')), where('environment', '==', companyEnv.value)
      ).withConverter(converter<FBUser>())
  );

  const users = useFirestore<FBUser>(usersQuery);

  // Add users
  async function createUser(user: Partial<FBUser>, environment: string): Promise<void> {
    const { email } = user;
    if (!email) {
      toast.error('Email is required');
      return;
    }
    const password = dayjs().valueOf().toString();
    let uid!: string;
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (result) {
        uid = (result as UserCredential).user.uid;
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`Error creating auth user: ${JSON.stringify(error)}`)
    }

    if (!uid) {
      return;
    }

    const userRef = doc(db, getRootCollection('USERS'), uid);
    try {
      await setDoc(userRef, {
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
        email: user.email,
        role: 'admin',
        environment,
      })
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`Error saving user profile: ${JSON.stringify(error)}`)
    }

    // Send password reset
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`Error trying to reset password ${JSON.stringify(error)}`)
      // TODO: Handle error
    }
  }

  const updateUser = async (user: FBUser) => {
    try {
      await updateDoc(doc(db, getRootCollection('USERS'), user.id), {
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
      });
      toast.success(
        `User details updated for ${user.firstName} ${user.lastName}`
      );
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`An error occured while updating user details: ${error}`);
    }
  }
  
  const removeUser = async (user: FBUser) => {
    // TODO: Remove user
  }

  const resetUserPassword = async (email: string) => {
    try {
      // Use the new Firebase function for password reset
      const sendPasswordReset = httpsCallable(funcs, 'emailAuth-sendPasswordReset');
      const result = await sendPasswordReset({ 
        email, 
        environmentId: companyEnv.value 
      });
      
      const response = result.data as { success: boolean; message: string };
      
      if (response.success) {
        toast.success('Password reset email sent successfully.');
      } else {
        toast.error(response.message || 'Failed to send password reset email');
      }
    } catch (error: any) {
      Sentry.captureException(error);
      toast.error(`An error occurred while resetting user password: ${error.message || error}`);
    }
  };

  const clear = () => {
    users.value = [];
  }


  return {
    resetUserPassword,
    users,
    createUser,
    updateUser,
    clear,
    // createOperator,
    // resetPassword,
  };
});